import Modal from "../Components/profileForm/Modal";
import FormTemplate from "../Components/profileForm/FormTemplate";
import NewProfileCard from "./ProfileCard/NewProfileCard";
import BusinessReviewPreview from "./ProfileCard/BusinessReviewPreview";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useParams } from "react-router-dom";

export default function CreateProfileForm({
  intro,
  setIntro,
  handleSubmit,
  user,
  modalOpen,
  setModalOpen,
  handleConfirm,
  answers,
  setAnswers,
  profilePicUrl = "",
}) {
  const { id } = useParams();
  const { setCardId } = useAuth();
  const navigate = useNavigate();
  const { userLoggedIn } = useAuth();
  if (userLoggedIn) {
    setIntro(false);
    setCardId(id);
  }
  const questions = [
    {
      id: "cardType",
      text: "How will you use this card?",
      type: "choice",
      autoAdvance: true,
      options: [
        {
          value: "individual",
          text: "Individual Networking",
          description: "Share your profile and connect with others",
        },
        {
          value: "business",
          text: "Market My Business",
          description: "Collect reviews, leads, or promote social media",
        },
        {
          value: "gift",
          text: "Gift Card",
          description: "Record a video message as a gift",
        },
      ],
    },
    {
      id: "businessPurpose",
      text: "What do you want to use it for?",
      type: "choice",
      autoAdvance: true,
      condition: {
        dependsOn: "cardType",
        value: "business",
      },
      options: [
        {
          value: "google-reviews",
          text: "Collect Google Reviews",
          description: "Get customers to leave reviews on Google",
        },
        {
          value: "leads",
          text: "Collect Leads",
          description: "Capture name and email from potential customers",
        },
        {
          value: "social-media",
          text: "Advertise Social Media",
          description: "Drive followers to your social profiles",
        },
      ],
    },
    // Gift-specific questions
    {
      id: "recipientName",
      text: "Who is this gift for?",
      placeholder: "e.g. Mom, John, Sarah",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "gift",
      },
    },
    {
      id: "senderName",
      text: "Who is this gift from?",
      placeholder: "e.g. Your Name, The Team",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "gift",
      },
    },
    {
      id: "occasion",
      text: "What is the occasion?",
      type: "choice",
      autoAdvance: true,
      condition: {
        dependsOn: "cardType",
        value: "gift",
      },
      options: [
        { value: "Birthday", text: "Birthday" },
        { value: "Anniversary", text: "Anniversary" },
        { value: "Wedding", text: "Wedding" },
        { value: "Thank You", text: "Thank You" },
        { value: "Just Because", text: "Just Because" },
        { value: "Christmas", text: "Christmas" },
        { value: "Holiday", text: "Holiday" },
        { value: "Other", text: "Other" },
      ],
    },
    {
      id: "otherOccasion",
      text: "What is the occasion?",
      placeholder: "e.g. Graduation, New Baby",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "occasion",
        value: "Other",
      },
    },
    {
      id: "endingMessage",
      text: "In short...",
      placeholder: "e.g. You are the best!",
      description: "One line summary of your message",
      type: "open",
      maxLen: 50,
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "gift",
      },
    },
    // Business-specific questions
    {
      id: "businessName",
      text: "What's your business name?",
      placeholder: "Acme Coffee Shop",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "business",
      },
    },
    {
      id: "googleReviewLink",
      text: "Google Review Link",
      placeholder:
        "Don't have a link? Click 'Find' button to search for your business",
      type: "open",
      description:
        "If you already have your Google review link, paste it here. Otherwise, click the 'Find' button to search for your business and we'll generate it automatically.",
      condition: {
        dependsOn: "businessPurpose",
        value: "google-reviews",
      },
    },
    {
      id: "linkedIn",
      text: "LinkedIn URL",
      placeholder: "your-profile",
      type: "open",
      defaultValue: "linkedin.com/in/",
      condition: {
        dependsOn: "businessPurpose",
        value: "social-media",
      },
    },
    {
      id: "instagram",
      text: "Instagram URL",
      placeholder: "yourhandle",
      type: "open",
      defaultValue: "instagram.com/",
      condition: {
        dependsOn: "businessPurpose",
        value: "social-media",
      },
    },
    {
      id: "xUrl",
      text: "X (Twitter) URL",
      placeholder: "yourhandle",
      type: "open",
      defaultValue: "x.com/",
      condition: {
        dependsOn: "businessPurpose",
        value: "social-media",
      },
    },
    {
      id: "facebook",
      text: "Facebook URL",
      placeholder: "your-page",
      type: "open",
      defaultValue: "facebook.com/",
      condition: {
        dependsOn: "businessPurpose",
        value: "social-media",
      },
    },
    {
      id: "tiktok",
      text: "TikTok URL",
      placeholder: "yourhandle",
      type: "open",
      defaultValue: "tiktok.com/@",
      condition: {
        dependsOn: "businessPurpose",
        value: "social-media",
      },
    },
    // Individual networking questions
    {
      id: "instructions",
      text: "Before we get started",
      description:
        "Remember to answer the questions with information you want to share with your new connections. Skip any questions you don't want to answer.",
      type: "info",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "firstName",
      text: "What is your first name?",
      placeholder: "Ava",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "lastName",
      text: "Last name",
      placeholder: "Brown",
      type: "open",
      required: "true",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "imageUrl",
      text: "Profile Picture",
      type: "file",
      description: answers.cardType === "business" 
        ? "Please upload your business logo" 
        : "Please upload a professional headshot",
      // store whether user has already uploaded a profile picture, so we can show its current value when editing
      currentUrl: profilePicUrl,
      condition: {
        dependsOn: "cardType",
        value: ["individual", "business"],
      },
    },
    {
      id: "email",
      text: "What's your email?",
      placeholder: "abrown@gmail.com",
      type: "open",
      condition: {
        dependsOn: "cardType",
        value: ["individual", "business"],
      },
    },
    {
      id: "phone",
      text: "Phone number",
      placeholder: "123-456-7890",
      type: "tel",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "headline",
      text: "Headline",
      placeholder: "Ex-Microsoft intern, incoming @ Google",
      type: "open",
      maxLen: 100,
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "college",
      text: "Where do you currently go to school?",
      placeholder: "University of California, Berkeley",
      type: "open",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "major",
      text: "What's your major?",
      placeholder: "Business Administration",
      description: "If you have more than one, list them all.",
      type: "open",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "linkedIn",
      text: "LinkedIn URL",
      placeholder: "your-profile",
      type: "open",
      defaultValue: "linkedin.com/in/",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "xUrl",
      text: "X URL",
      placeholder: "yourhandle",
      type: "open",
      defaultValue: "x.com/",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "instagram",
      text: "Instagram URL",
      placeholder: "yourhandle",
      type: "open",
      defaultValue: "instagram.com/",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "github",
      text: "GitHub link",
      placeholder: "username",
      type: "open",
      defaultValue: "github.com/",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "portfolio",
      text: "Portfolio Website",
      placeholder: "https://www.your-website.com",
      type: "open",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "resume",
      description: "Please provide a link to your resume",
      text: "Resume",
      placeholder: "https://docs.google.com/document/...",
      type: "open",
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
    {
      id: "additional",
      text: "Anything else you want to share on your profile?",
      description:
        "Share your experience, interests, achievements and/or favorite Spiderman actor.",
      placeholder: "Personal project links, shoutouts, media coverage etc.",
      type: "open",
      maxLen: 250,
      condition: {
        dependsOn: "cardType",
        value: "individual",
      },
    },
  ];
  if (intro) {
    return (
      <div className="flex flex-col h-full justify-center items-center gap-8 m-4 ">
        <div className="text-2xl font-bold text-gray-800 text-center">
          Welcome to Penpal
        </div>
        <div className="text-lg text-gray-600 text-center">
          Let's create your profile quickly so you can start connecting
        </div>
        <button
          className="rounded-2xl px-4 py-2 border-black border hover:border-blue-800 group"
          onClick={() => {
            setIntro(false);
            navigate(`/register/${id}`);
          }}
        >
          <div className="group-hover:text-blue-800">Create Profile</div>
        </button>
      </div>
    );
  }

  // Determine which preview to show based on card type
  const getPreviewComponent = () => {
    if (
      answers.cardType === "business" &&
      answers.businessPurpose === "google-reviews"
    ) {
      return <BusinessReviewPreview answers={answers} />;
    }
    if (answers.cardType === "gift") {
      return (
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8 text-red-500"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Gift for {answers.recipientName}
          </h3>
          <p className="text-gray-500 mb-2">
            From: {answers.senderName} • Occasion:{" "}
            {answers.occasion === "Other"
              ? answers.otherOccasion
              : answers.occasion}
          </p>
          <p className="text-lg font-serif italic text-gray-700 mb-6">
            "{answers.endingMessage}"
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
            You are about to create a gift session. Next, you'll be able to record your message and invite others.
          </div>
        </div>
      );
    }
    // Default to individual networking preview
    return <NewProfileCard person={answers} />;
  };

  const getModalTitle = () => {
    if (
      answers.cardType === "business" &&
      answers.businessPurpose === "google-reviews"
    ) {
      return "Confirm your business details";
    }
    if (answers.cardType === "gift") {
      return "Confirm Gift Details";
    }
    return "Confirm your details";
  };

  return (
    <div className="flex flex-col h-full justify-center items-center gap-8 m-4 md:w-2/3">
      <Modal
        title={getModalTitle()}
        person={getPreviewComponent()}
        confirm={() => handleConfirm()}
        open={modalOpen}
        setOpen={setModalOpen}
      />
      <FormTemplate
        questions={questions}
        onSubmit={handleSubmit}
        answers={answers}
        setAnswers={setAnswers}
      />
    </div>
  );
}
